<script setup lang="ts">
  import { computed, h, nextTick, onBeforeUnmount, onMounted, provide, reactive, ref } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, message, Drawer, Modal, Input, Spin, Tooltip } from 'ant-design-vue'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CodeOutlined,
    FileSearchOutlined,
    WarningFilled,
    ExclamationCircleOutlined
  } from '@ant-design/icons-vue'
  import RuleSelectionModal from './components/RuleSelectionModal.vue'
  import RuleConfiguration from './components/RuleConfiguration.vue'
  import { useUser } from '@/composables/useUser'
  import { Loader2, Trash2, SparklesIcon, Search, Cog, ChevronRight } from 'lucide-vue-next'
  import { watch } from 'vue'
  import { DataCleaningService } from './services/DataCleaningService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import RuleJsonManagement from './components/RuleJsonManagement.vue'
  import FullScreenModal from '@/components/FullScreenModal.vue'
  import DataViewer from '@/components/DataViewer.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { DataCleaningSearchService } from './services/DataCleaningSearchService'
  import JsonRuleConfiguration from './components/JsonRuleConfiguration.vue'
  import { DataCleaningJsonRuleConfigurationService } from './services/DataCleaningJsonRuleConfigurationService'
  import { DataCleaningJsonParsingService } from './services/DataCleaningJsonParsingService'
  import { DataCleaningTypeGuardService } from './services/DataCleaningTypeGuardService'
  import { Rule } from './models/Rule'
  import { RuleSchema } from './models/RuleSchema'
  import { InjectionService } from '@/shared/services/InjectionService'
  import {
    DataCleaningApiService,
    IDataCleaningApiService
  } from './services/DataCleaningApiService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { DataCleaningUiService } from './services/DataCleaningUiService'
  import { RuleSchemaUiFieldGroup } from './models/RuleSchemaUi'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import { AnalysisWizardStepType } from '../AnalysisWizard/models/AnyalsisWizardStepType'
  import { AnalysisWizardNavigationService } from '../AnalysisWizard/services/AnalysisWizardNavigationService'
  import { AnalysisWizardModel } from '../AnalysisWizard/models/AnalysisWizardModel'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AvailableColumnServiceBase } from './services/AvailableColumnServiceBase'
  import { AvailableColumn } from './models/AvailableColumn'
  import { AplosFile } from '../FileManager/models/AplosFile'
  import { GetRulesArgs } from './models/GetRulesArgs'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { GetAplosFileArgs } from '../FileManager/models/GetAplosFileArgs'
  import { AplosFileApiService } from '../FileManager/services/AplosFileApiService'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import FileManager from '../FileManager/components/FileManager.vue'
  import { AvailableColumnsService } from './services/AvailableColumnsService'
  import { isCleanedFile } from '../FileManager/services/AplosFileUtilities'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
  import { IExecutionPhaseService } from '@/shared/Executions/services/ExecutionPhaseService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'
  import { RulesValidationService } from './services/RulesValidationService'

  interface IDataCleaningProps {
    analysisFile?: AplosFile
    rules?: Array<Rule> // flattened
    cleanedFile?: AplosFile
    dataCleaningExecution?: Execution // stores last known queued execution for this step
    fileSelectionDisabled?: boolean
    wizardModel?: AnalysisWizardModel
    showLoading?: boolean
  }

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )

  // TODO: consider passing this in as a property or passing to a method instead of binding in this component that can be
  // rendered outside the context of a wizard
  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    'AnalysisWizardNavigationService'
  )

  const executionPhaseService: IExecutionPhaseService =
    InjectionService.injectStrict('IExecutionPhaseService')

  const rulesApiService: IDataCleaningApiService = InjectionService.injectStrict(
    DataCleaningApiService.injectableKey
  )

  const dataCleaningJsonParsingService: DataCleaningJsonParsingService =
    InjectionService.injectStrict(DataCleaningJsonParsingService.injectableKey)

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const availableColumnsService: AvailableColumnServiceBase = InjectionService.injectStrict(
    AvailableColumnsService.injectableKey
  )

  const aplosFileApiService: AplosFileApiService = InjectionService.injectStrict(
    AplosFileApiService.injectableKey
  )

  const rulesValidationService: RulesValidationService = InjectionService.injectStrict(
    RulesValidationService.injectableKey
  )

  const props = defineProps<IDataCleaningProps>()

  const emit = defineEmits<{
    (e: 'update:rules', value: Rule[]): void
    (e: 'update:cleanedFile', value?: AplosFile): void
    (e: 'update:dataCleaningExecution', value?: Execution): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const execution = ref<Execution | undefined>(props.dataCleaningExecution)

  const isExecuting = computed(
    () =>
      [
        ExecutionStatusType.Succeeded,
        ExecutionStatusType.Cancelled,
        ExecutionStatusType.Failed,
        ExecutionStatusType.Unknown
      ].indexOf(execution.value?.status ?? ExecutionStatusType.Unknown) === -1
  )

  watch(
    () => props.dataCleaningExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  const isProcessingRules = ref<boolean>(false)
  const isLoadingAplosFile = ref<boolean>(false)
  const isLoadingAvailableRules = ref<boolean>(false)
  const hasErrorLoadingData = ref<boolean>(false)
  const availableRuleSchemas = ref<RuleSchema[]>([])

  // Reactive data for active rules
  const activeRules = reactive<Rule[]>([])
  const selectedRule = ref<Rule | null>(null)
  const rulesValidationResult = computed(() =>
    rulesValidationService.validateRules([...activeRules])
  )
  const rulesHaveValidationErrors = computed(() => !rulesValidationResult.value.isValid)
  const showRuleSelection = ref(false)
  const showJsonManagement = ref(false)
  const dataViewerModalOpen = ref(false)
  const cleanedDataViewerModalOpen = ref(false)
  // --- File selection state ---
  const showFileManager = ref(false)
  const selectedFile = ref<AplosFile | null>(null)

  const cleanedFile = ref<AplosFile | null>(props.cleanedFile ?? null)
  const outputFileName = ref<string>('')

  const outputFileNameError = computed(() => {
    const fileName = DataCleaningService.scrubOutputFileName(outputFileName.value.trim())
    if (
      selectedFile.value?.name != null &&
      fileName == DataCleaningService.scrubOutputFileName(selectedFile.value.name)
    )
      return 'Cannot be the same as input file.'
    // allow letters, numbers, underscores, parentheses, hyphens, dots, and spaces
    if (fileName?.length && !/^[\w\-\.\(\) ]+$/.test(outputFileName.value))
      return 'Invalid file name. Only letters, numbers, spaces, underscores, parenthesis, hyphens, and dots allowed.'
    return ''
  })

  // --- Available columns for column selection ---
  const sourceColumns = ref<Array<AvailableColumn>>([])
  const availableColumns = computed(() =>
    availableColumnsService.getAvailableColumnsForRule(
      selectedRule.value,
      activeRules,
      sourceColumns.value
    )
  )

  // Helper to load columns from file payload or API
  const loadAvailableColumns = async (analysisFile?: AplosFile) => {
    sourceColumns.value = []
    if (!selectedFile.value?.id) return

    let columnNames = analysisFile?.metadata?.columnNames

    if (!columnNames?.length) {
      let filePayload: AplosFile | null = null
      try {
        filePayload = (selectedFile as any).lastPayload || null
      } catch {}
      if (
        filePayload &&
        Array.isArray(filePayload?.metadata?.columnNames) &&
        filePayload?.metadata?.columnNames?.length > 0
      ) {
        columnNames = filePayload.metadata.columnNames
      }
    }

    if (columnNames?.length) {
      sourceColumns.value = columnNames.map((name) => ({
        value: name,
        label: name
      }))
    }
  }

  const loadAvailableRulesSchemas = async () => {
    hasErrorLoadingData.value = false
    isLoadingAvailableRules.value = true
    try {
      const ruleSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.RuleSchema,
        customSchemaStore
      )

      availableRuleSchemas.value = ruleSchemas
    } catch (error) {
      hasErrorLoadingData.value = true
      message.error('Failed to load available rules.')
    } finally {
      isLoadingAvailableRules.value = false
    }
  }

  const draggedRule = ref<Rule | null>(null)
  const highlightedRuleId = ref<string | null>(null)
  const deletingRuleId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activeRuleViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activeRuleEditConfiguration = ref<{} | null>(null)

  watch(
    selectedRule,
    (newVal) => {
      if (newVal) {
        // Update tracked flattented JSON for management slideout
        const flattenedRules = DataCleaningService.getFlattendRules([newVal])
        activeRuleEditConfiguration.value = flattenedRules?.length ? flattenedRules[0] : null
      }
    },
    { deep: false }
  )

  // Ensure last updated (UI) rule configuration is flattened and update
  // Json editor model ref
  const onRuleViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (selectedRule.value == null || activeRuleEditConfiguration.value == null) return

    if (activeMode === 'edit') {
      const flattenedRules = DataCleaningService.getFlattendRules([selectedRule.value])
      activeRuleEditConfiguration.value = flattenedRules?.length ? flattenedRules[0] : null
    }
  }

  // Add a new rule from the selected schema
  const addRule = (ruleSchema: RuleSchema): Rule => {
    const newRule = DataCleaningService.createRuleFromRuleSchema(ruleSchema)
    activeRules.push(newRule)
    selectedRule.value = newRule
    updateSharedModelOnRuleChange()
    message.success(`Added rule: ${ruleSchema.display_name}`, defaultMessageDurationSeconds)
    return newRule
  }

  // Select a rule to configure
  const selectRule = (rule: Rule) => {
    selectedRule.value = rule
  }

  // Remove a rule
  const removeRuleTimeoutMs: number = 600
  const removeRule = (rule: Rule) => {
    // Start deletion animation
    animateRuleDeletion(rule.id as string)
    message.success(`Removed "${rule.display_name}"`, defaultMessageDurationSeconds)
  }

  const notifyConfigChangedStream$ = new Subject<void>()
  const batchNotifyConfigChangedSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Raw Json Edits are parsed back through rule initialization process
  // similarly to how we import json files
  const handleJsonEditorConfigurationChange = () => {
    if (selectedRule.value != null && activeRuleEditConfiguration.value != null) {
      const editConfigurationJson = JSON.stringify([activeRuleEditConfiguration.value])
      const result = dataCleaningJsonParsingService.tryParseEntitiesFromJson(
        editConfigurationJson,
        availableRuleSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingRuleIndex = activeRules.findIndex((x) => x.id === selectedRule.value?.id)
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingRuleIndex <= -1
        )
          throw new Error('Error updating rules from Json Rule Editor value!')
        const updatedRule = (result.parsedEntities as Array<Rule>)[0]
        const existingRule = activeRules[existingRuleIndex]
        Object.assign(existingRule, { ...updatedRule, id: existingRule.id })
        selectedRule.value = existingRule
      }
    }
  }

  // Ensure validation logic for wizard nav updated based on realtime shared model
  const updateSharedModelOnRuleChange = () => {
    if (!isExecuting.value) {
      emit('update:dataCleaningExecution', undefined)
      emit('update:cleanedFile', undefined)
    }
    const flattenedRules = DataCleaningService.getFlattendRules(activeRules)
    emit('update:rules', flattenedRules)
  }

  // Handle rule configuration updates
  const handleConfigurationUpdate = async (
    rule: Rule,
    config: Record<string, any>,
    ruleTypeChange?: boolean
  ) => {
    const ruleSearchResults = DataCleaningSearchService.findRuleByIdRecursive(
      activeRules,
      rule.id as string
    )
    if (ruleSearchResults != null) {
      const topLevelParentRuleIndex = activeRules.findIndex(
        (r) => r.id === ruleSearchResults.topLevelParent?.id
      )
      if (topLevelParentRuleIndex > -1) {
        // For ruleTypeChange we initialize new Rule instance and
        // either splice top-level activeRules or find and set the configuration in tree of rules
        if (ruleTypeChange === true) {
          const newRuleSchema = DataCleaningService.tryGetRuleSchemaByType(
            rule._type,
            availableRuleSchemas.value
          )
          if (newRuleSchema == null)
            throw new Error(`Error getting RuleSchema for rule.type: ${rule.type}!`)
          selectedRule.value = null
          // Create new rule from schema resetting any existing metadata
          let newRule = DataCleaningService.createRuleFromRuleSchema(newRuleSchema)
          // Initialize any sub-rule metadata if neccessary
          newRule.description = rule.configuration?.description
          newRule.configuration = { ...config }
          if (
            ruleSearchResults.topLevelParent != null &&
            ruleSearchResults.topLevelParent !== ruleSearchResults.rule &&
            ruleSearchResults.path?.length > 1
          ) {
            // Recursive search to find and update the view model
            const relativePath = ruleSearchResults.path.slice(1)
            DataCleaningSearchService.setRuleAtPath(
              ruleSearchResults.topLevelParent,
              relativePath,
              newRule
            )
          } else {
            activeRules.splice(topLevelParentRuleIndex, 1, newRule)
          }
          notifyConfigChangedStream$.next()
          nextTick(() => {
            selectedRule.value = activeRules[topLevelParentRuleIndex]
          })
        } else if (ruleSearchResults.topLevelParent === ruleSearchResults.rule) {
          // No new rule changes, just commit updated configuration
          activeRules[topLevelParentRuleIndex].configuration = { ...config }
          activeRules[topLevelParentRuleIndex]._type = config._type
          notifyConfigChangedStream$.next()
        }

        updateSharedModelOnRuleChange()
      }
    }
  }

  // Highlight animation for moved rules
  const highlightMovedRule = (ruleId: string) => {
    highlightedRuleId.value = ruleId
    // Clear highlight after animation completes
    setTimeout(() => {
      highlightedRuleId.value = null
    }, 1500)
  }

  // Deletion animation for removed rules
  const animateRuleDeletion = (ruleId: string) => {
    deletingRuleId.value = ruleId
    // Remove rule after animation completes
    setTimeout(() => {
      activeRules.splice(0, activeRules.length, ...activeRules.filter((r) => r.id !== ruleId))
      deletingRuleId.value = null
      // Clear selection if deleted rule was selected
      if (selectedRule.value?.id === ruleId) {
        if (activeRules?.length > 0) {
          selectedRule.value = activeRules[0]
        } else {
          selectedRule.value = null
        }
      }

      updateSharedModelOnRuleChange()
    }, removeRuleTimeoutMs) // Shorter duration for deletion
  }

  // Show the rule selection modal
  const showAddRuleModal = () => {
    showRuleSelection.value = true
  }

  const initializeOutputFileName = (fileName: string = ''): string => {
    const cleanedFileSuffix: string = '-cleaned'

    const lastDotIndex = fileName.lastIndexOf('.')

    if (lastDotIndex === -1 || lastDotIndex === 0 || lastDotIndex === fileName.length - 1) {
      throw new Error('Invalid filename. Must contain a valid extension.')
    }

    const name = fileName.substring(0, lastDotIndex)
    const ext = fileName.substring(lastDotIndex)

    const cleanedFileName = `${name}${cleanedFileSuffix}${ext}`
    return cleanedFileName
  }

  // Handle file selection from FileManager
  const handleFileSelected = async (file: AplosFile, rootFile?: AplosFile) => {
    // file is selected (can be derived/cleaned) root is original source
    showFileManager.value = false

    if (file.id && file.name) {
      // For cleaned files we load the original
      let selectedDataFile: AplosFile = file
      outputFileName.value = initializeOutputFileName(selectedDataFile.name)
      const cleanedFile = isCleanedFile(file)
      if (cleanedFile) {
        if (rootFile == null) throw Error('missing expected rootFile for cleaned input file!')
        selectedDataFile = rootFile

        const user = useUser()
        await user.initialize()
        const args = new GetRulesArgs({
          tenantId: user.user.tenant_id as string,
          userId: user.user.id as string,
          fileId: file.id
        })
        const flatRules = await rulesApiService.getRules(args)
        initializeFlatRules(flatRules)
      }

      selectedFile.value = selectedDataFile
      // Attach payload for column_names if present
      ;(selectedFile as any).lastPayload = selectedDataFile

      message.success(`Selected file: ${selectedDataFile.name}`, defaultMessageDurationSeconds)
      // Load columns after selecting file
      loadAvailableColumns(selectedDataFile)
    } else {
      message.error('Invalid file selected', defaultMessageDurationSeconds)
    }
  }

  // Initialize flatRules or load for cleanedFileId and
  const initializeFlatRules = (flatRules?: Array<Rule>) => {
    try {
      isLoadingAvailableRules.value = true
      const initializedRules: Array<Rule> = []
      let inputRules = flatRules || []
      inputRules.forEach((flatRule: Rule) => {
        const initializedRule = dataCleaningJsonParsingService.initializeEntity(
          flatRule,
          availableRuleSchemas.value,
          1
        )
        initializedRules.push(initializedRule)
      })
      // Replace the contents of the reactive array so Vue picks up the change
      activeRules.splice(0, activeRules.length, ...initializedRules)
      // Set the first rule as active
      if (activeRules.length > 0) selectedRule.value = activeRules[0]
      isLoadingAvailableRules.value = false
    } catch {
      message.error('Failed to load analysis file.')
    } finally {
      isLoadingAplosFile.value = false
    }
  }

  const viewFileData = async () => {
    if (selectedFile.value?.id != null) {
      dataViewerModalOpen.value = true
    }
  }

  const closeDataViewerModal = () => {
    dataViewerModalOpen.value = false
  }

  const viewCleanedFileData = async () => {
    if (cleanedFile.value?.id != null) {
      cleanedDataViewerModalOpen.value = true
    }
  }

  const closeCleanedDataViewerModal = () => {
    cleanedDataViewerModalOpen.value = false
  }

  // Watch for file changes to reload columns
  watch(selectedFile, (newFile) => {
    if (newFile?.id) {
      loadAvailableColumns(newFile)
    } else {
      sourceColumns.value = []
    }
  })

  // Show validation error messages whenever the validation result changes
  watch(rulesValidationResult, (result) => {
    if (!result.isValid && result.errors.length > 0) {
      message.error(result.errors.join('\n'), defaultMessageDurationSeconds)
    }
  })

  // JSON Export Functions
  const showJsonManagementDrawer = () => {
    showJsonManagement.value = true
  }

  // Move rule up in the list
  const moveRuleUp = (rule: Rule) => {
    const currentIndex = activeRules.findIndex((r) => r.id === rule.id)
    if (currentIndex > 0) {
      // Swap with the previous rule
      const temp = activeRules[currentIndex]
      activeRules[currentIndex] = activeRules[currentIndex - 1]
      activeRules[currentIndex - 1] = temp

      // Highlight the moved rule
      highlightMovedRule(rule.id as string)

      message.success(
        `Moved "${rule.display_name}" up to position ${currentIndex}`,
        defaultMessageDurationSeconds
      )
    }
  }

  // Move rule down in the list
  const moveRuleDown = (rule: Rule) => {
    const currentIndex = activeRules.findIndex((r) => r.id === rule.id)
    if (currentIndex < activeRules.length - 1) {
      // Swap with the next rule
      const temp = activeRules[currentIndex]
      activeRules[currentIndex] = activeRules[currentIndex + 1]
      activeRules[currentIndex + 1] = temp

      // Highlight the moved rule
      highlightMovedRule(rule.id as string)

      message.success(
        `Moved "${rule.display_name}" down to position ${currentIndex + 2}`,
        defaultMessageDurationSeconds
      )
    }
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, rule: Rule) => {
    draggedRule.value = rule
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', rule.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()

    if (draggedRule.value) {
      const draggedIndex = activeRules.findIndex((r) => r.id === draggedRule.value!.id)

      if (draggedIndex !== targetIndex) {
        // Remove the dragged rule from its current position
        const [movedRule] = activeRules.splice(draggedIndex, 1)
        // Insert it at the new position
        activeRules.splice(targetIndex, 0, movedRule)

        // Highlight the moved rule
        highlightMovedRule(draggedRule.value.id as string)

        message.success(
          `Moved "${draggedRule.value.display_name}" to position ${targetIndex + 1}`,
          defaultMessageDurationSeconds
        )
      }
    }

    // Clear drag state
    draggedRule.value = null
    // Remove drop zone highlighting from all cards
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    // Reset drag state
    draggedRule.value = null
    dragOverIndex.value = -1
  }

  const clearFileSelection = () => {
    selectedFile.value = null
    outputFileName.value = ''
    if (activeRules?.length > 0) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Clear existing rules?',
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          activeRules.splice(0, activeRules.length)
          selectedRule.value = null
        },
        onCancel() {}
      })
    }
  }

  const onSubmitDataForCleaningClicked = async () => {
    // TODO: better way to determine if this is in wizard or standalone
    // If we've already cleaned data, notify taht this will reseet
    if (props.wizardModel && cleanedFile.value != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Processing data cleaning rules reset any your PK Analysis and Post Processing Configuration.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitDataForCleaning()
        },
        onCancel() {}
      })
    } else {
      submitDataForCleaning()
    }
  }

  const submitDataForCleaning = async () => {
    isProcessingRules.value = true

    const auth = useUser()
    await auth.initialize()

    const flattenedRules = DataCleaningService.getFlattendRules(activeRules)

    const args = DataCleaningService.createQueueDataCleaningArgs(
      selectedFile.value?.id as string,
      flattenedRules,
      outputFileName.value
    )

    try {
      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Processing data cleaning rules',
        props.wizardModel,
        rulesApiService.processRules.bind(rulesApiService),
        auth?.user?.id as string,
        auth?.user?.tenant_id as string,
        args
      )

      const processRulesResponse = response?.data as QueueExecutionResponse
      if (!processRulesResponse?.executionId) throw Error('An unexpected error has occurred!')
      const successMessage = 'Processing data cleaning rules.'
      /* processRulesResponse?.message?.length
          //? processRulesResponse.message
          : 'Data cleaning queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processRulesResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:dataCleaningExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      // Normalize different possible error shapes into string messages
      let errors: string[] = ['No specific errors encountered.']

      const respErrors = error?.response?.data?.errors

      if (Array.isArray(respErrors) && respErrors.length > 0) {
        errors = respErrors.map((item: any) => {
          if (typeof item === 'string') return item
          if (item && typeof item === 'object')
            return item.error ?? item.message ?? JSON.stringify(item)
          return String(item)
        })
      } else if (error?.message) {
        // Fallback to error.message if present
        errors = [String(error.message)]
      }

      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered processing data cleaning rules:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, execution: Execution) => {
    emit('update:dataCleaningExecution', execution)
  }

  const onExecutionCompleted = async (success: boolean, execution: Execution) => {
    emit('update:dataCleaningExecution', execution)
    isProcessingRules.value = false;
    if (success) {
      const dataCleaningPhaseOutput = executionPhaseService.tryGetOutputForPhaseType(
        execution,
        ExecutionPhaseType.DataCleaning
      )
      const cleanedFileId = dataCleaningPhaseOutput?.fileId

      if (!cleanedFileId)
        throw Error('Error getting cleaned file output for data cleaning execution!')

      // After return is success/complete continue with Getting cleaned datases
      // Get cleanedFile for processed rules
      await AnalysisWizardService.runAsyncTaskWithLoading(
        'Getting cleaned dataset',
        props.wizardModel,
        async () => {
          const user = useUser()
          await user.initialize()
          const args = new GetAplosFileArgs({
            tenantId: user.user.tenant_id as string,
            userId: user.user.id as string,
            fileId: cleanedFileId
          })
          const response = await aplosFileApiService.getFile(args)
          const file: AplosFile = response.data
          cleanedFile.value = file
          outputFileName.value = file.name!
          emit('update:cleanedFile', file)
        }
      )

      // Commit rules to sharedModel
      const flattenedRules = DataCleaningService.getFlattendRules(activeRules)
      emit('update:rules', flattenedRules)

      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Processing data cleaning rules')
    }
  }

  const onJsonManagementRulesApplied = () => {
    selectedRule.value = activeRules?.length ? activeRules[0] : null
    updateSharedModelOnRuleChange()
  }

  // For custom or unknown types disable superfluous input tab and just rely on Json Editor Tab
  const inputTabDisabled = computed(() => {
    if (
      selectedRule.value == null ||
      selectedRule.value._type === DataCleaningService.CUSTOM_RULE_TYPE
    )
      return true
    const activeSchemaType = DataCleaningService.tryGetRuleSchemaByType(
      selectedRule.value._type as string,
      availableRuleSchemas.value
    )
    return (
      activeSchemaType == null || activeSchemaType.type === DataCleaningService.CUSTOM_RULE_TYPE
    )
  })

  // Ensure we preselect the edit tab if view is disabled
  // but only if the mode is not already edit
  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activeRuleViewMode.value = { mode: 'edit' }
      } else if (activeRuleViewMode.value.mode !== 'edit') {
        activeRuleViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  watch(
    () => props.analysisFile,
    async (analysisFile?: AplosFile) => {
      selectedFile.value = analysisFile as AplosFile
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.analysisFile == null || props.cleanedFile == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (stepStatusMessage == null) {
        if (props.analysisFile == null) stepStatusMessage = 'Missing input dataset'
        else if (props.cleanedFile == null) stepStatusMessage = 'No cleaned data'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    await loadAvailableRulesSchemas()

    updateWizardStepStatus()

    // Watch input flat rules and ensure initialized (after schemas loaded)
    initializeFlatRules(props.rules)

    const cleanedFileName = props.cleanedFile?.name
    const analysisFileName = props.analysisFile?.name
    if (cleanedFileName != null) {
      outputFileName.value = cleanedFileName
    } else if (analysisFileName != null) {
      outputFileName.value = initializeOutputFileName(analysisFileName)
    }

    watch(
      activeRules,
      () => {
        if (!rulesValidationResult.value?.isValid) {
          updateWizardStepStatus(WizardStepStatusType.Errors, rulesValidationResult.value.errors.join('\n'))
        } else {
          updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
        }
      },
      { immediate: false }
    )
  })

  onBeforeUnmount(() => {
    batchNotifyConfigChangedSubscription?.unsubscribe()
  })

  // Add activeRuleTabKey for dynamic tab selection
  const activeRuleTabKey = ref<string>('view')

  // Add uiFieldGroups computed property
  const uiFieldGroups = computed(() => {
    return DataCleaningUiService.getRuleSchemaUiFieldGroups(selectedRule.value?.schema)
  })

  // Ensure first tab is selected on update of selected rule
  // persist JSON tab selection if already active
  watch(
    [selectedRule, uiFieldGroups],
    ([rule, groups]) => {
      if (rule != null && groups != null && groups.length > 0) {
        // If on 'edit' tab, do not change
        if (activeRuleTabKey.value === 'edit') return
        // If the current tab key is not in the group, or empty, set to first group
        if (!groups.some((g: RuleSchemaUiFieldGroup) => g.name === activeRuleTabKey.value)) {
          activeRuleTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  // --- Add this watcher to reset tab selection on selectedRule change ---
  watch(selectedRule, (newRule, oldRule) => {
    // Only reset if the rule actually changed (not just updated in place)
    if (newRule && newRule !== oldRule) {
      // If uiFieldGroups exist and have at least one group, select the first group tab
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activeRuleTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        // If Input tab is not disabled, select Input tab
        activeRuleViewMode.value = { mode: 'view' }
      } else {
        // Otherwise, select JSON tab
        activeRuleViewMode.value = { mode: 'edit' }
      }
    }
  })

  const goToWizardStepByType = (stepType: AnalysisWizardStepType) => {
    if (props.wizardModel != null) {
      wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
    }
  }

  const viewCleanedDataDisabled = computed(
    () => cleanedFile.value == null || isProcessingRules.value
  )

  const goToAnalysisConfigDisabled = computed(() => {
    const shared = props.wizardModel?.sharedModel
    if (shared) {
      return !wizardNavigationService.getIsStepAvailable(
        AnalysisWizardStepType.AnalysisConfiguration,
        shared
      )?.isAvailable
    }
    return true
  })

  const { is2XLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activeRules,
    addRule,
    removeRule,
    removeRuleTimeoutMs,
    moveRuleUp,
    moveRuleDown
  })
</script>

<template>
  <div
    :class="[
      'data-cleaning-container',
      'h-full',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'data-cleaning-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter :class="isFitToBrowser ? 'h-full' : ''">
      <!-- Left Pane: Active Rules -->
      <SplitterPanel :size="30" style="min-width: 350px">
        <div :class="['flex', 'flex-col', 'border-r-2', 'shadow-sm', 'h-full']">
          <div class="border-b p-4 shadow-sm">
            <!-- File Selection -->
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Source File</label>
              <div class="flex items-center gap-2">
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  :disabled="
                    isLoadingAplosFile ||
                    isLoadingAvailableRules ||
                    fileSelectionDisabled ||
                    showLoading
                  "
                  block
                  @click="showFileManager = true"
                  :title="selectedFile ? 'Change selected file' : 'Select a file'"
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedFile?.name ?? 'Select File' }}
                  </span>
                </Button>

                <a-tooltip>
                  <template #title>
                    {{ 'Loading file...' }}
                  </template>
                  <Loader2 v-if="isLoadingAplosFile" class="mr-2 size-4 animate-spin" />
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View Data' }}
                  </template>
                  <Button
                    v-if="selectedFile && !isLoadingAplosFile"
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="viewFileData"
                    title="View Data"
                    tooltip="View Data"
                  >
                    <FileSearchOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>

                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>

                  <Button
                    v-if="selectedFile && !isLoadingAplosFile && !fileSelectionDisabled"
                    shape="circle"
                    size="large"
                    type="default"
                    danger
                    class="flex items-center justify-center"
                    @click="clearFileSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Output File Name</label>
              <a-tooltip placement="right">
                <template #title>
                  {{ 'Defaults to input file name suffixed with "-cleaned"' }}
                </template>
                <Input
                  v-model:value="outputFileName"
                  :disabled="
                    selectedFile == null ||
                    isLoadingAplosFile ||
                    isLoadingAvailableRules ||
                    showLoading
                  "
                  placeholder="Optionally, enter output file name"
                  size="large"
                  :status="outputFileNameError ? 'error' : ''"
                >
                  <template #suffix>
                    <Tooltip
                      v-if="outputFileNameError"
                      placement="left"
                      :title="outputFileNameError"
                    >
                      <ExclamationCircleOutlined style="color: red" />
                    </Tooltip>
                  </template>
                </Input>
              </a-tooltip>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Data Cleaning Rules</h3>
                <p class="mt-1 text-sm">
                  {{ activeRules.length }}
                  {{ activeRules.length === 1 ? 'rule' : 'rules' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip>
                  <template #title>
                    {{ 'Add New Rule' }}
                  </template>
                  <Button
                    :disabled="selectedFile == null || isProcessingRules"
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showAddRuleModal"
                    title="Add New Rule"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagementDrawer"
                    title="View/Export JSON"
                    :disabled="selectedFile == null || isProcessingRules"
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>
          <div class="rules-list-container flex-1 p-4">
            <!-- Rules List -->
            <div class="rules-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <!-- Empty State -->
              <div
                v-if="activeRules.length === 0"
                class="flex h-full flex-col items-center justify-start py-12 text-center"
              >
                <div class="mb-6">
                  <div
                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                  >
                    <svg
                      class="h-10 w-10 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      ></path>
                    </svg>
                  </div>
                </div>
                <h3 class="mb-2 text-lg font-semibold">No Data Cleaning Rules Yet</h3>

                <!-- Quick Tips -->
                <div
                  class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                >
                  <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                    💡 Quick Tips
                  </h4>
                  <ul class="text-blue-800: space-y-1 text-xs dark:text-white">
                    <li>• Rules execute in order from top to bottom</li>
                    <li>• Use drag & drop or arrows to reorder rules</li>
                    <li>• Configure each rule in the right panel</li>
                  </ul>
                </div>
              </div>

              <!-- Active Rules List -->
              <div v-else class="space-y-1.5 px-1 pb-5">
                <Card
                  v-for="(rule, index) in activeRules"
                  :key="rule.id"
                  :class="[
                    'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                    {
                      'ring-2 ring-blue-500': selectedRule?.id === rule.id,
                      'opacity-50': draggedRule?.id === rule.id,
                      'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                        dragOverIndex === index,
                      'rule-moved-highlight': highlightedRuleId === rule.id,
                      'rule-deleting': deletingRuleId === rule.id
                    }
                  ]"
                  :bodyStyle="{
                    padding: '18px'
                  }"
                  draggable="true"
                  @click="selectRule(rule)"
                  @dragstart="handleDragStart($event, rule)"
                  @dragover="handleDragOver($event, index)"
                  @dragleave="handleDragLeave"
                  @drop="handleDrop($event, index)"
                  @dragend="handleDragEnd"
                >
                  <div>
                    <div class="flex items-start justify-between">
                      <!-- Drag Handle -->
                      <div class="mr-3 flex items-center">
                        <HolderOutlined class="cursor-grab active:cursor-grabbing" />
                      </div>

                      <div class="min-w-0 flex-1">
                        <h4 class="truncate text-sm font-medium leading-tight">
                          {{ rule.display_name }}
                        </h4>
                        <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                          {{ rule.configuration?.description ?? '&nbsp;' }}
                        </p>
                      </div>

                      <!-- Rule Controls -->
                      <div
                        class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <!-- Move Up Button -->
                        <Button
                          type="text"
                          size="small"
                          :disabled="activeRules.findIndex((r) => r.id === rule.id) === 0"
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveRuleUp(rule)"
                        >
                          <ArrowUpOutlined class="text-xs" />
                        </Button>

                        <!-- Move Down Button -->
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activeRules.findIndex((r) => r.id === rule.id) ===
                            activeRules.length - 1
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveRuleDown(rule)"
                        >
                          <ArrowDownOutlined class="text-xs" />
                        </Button>

                        <!-- Delete Button -->
                        <Button
                          type="link"
                          size="small"
                          danger
                          class="flex h-8 w-8 items-center justify-center"
                          @click.stop="removeRule(rule)"
                        >
                          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SplitterPanel>

      <!-- Right Pane: Rule Configuration -->
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedRule
                    ? `Configure: ${selectedRule.display_name}`
                    : 'Data Processing Configuration'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedFile != null
                    ? selectedRule != null
                      ? 'Configure the selected rule parameters below'
                      : 'Select a rule from the left panel to configure its settings'
                    : 'Select a file to begin configuring rules'
                }}
              </p>
            </div>
            <div class="ml-4 flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Preview Cleaned Data -->
              <a-tooltip>
                <template #title>
                  {{
                    selectedFile == null || !activeRules?.length
                      ? 'Submit configured rules to preview cleaned data.'
                      : 'Preview cleaned data'
                  }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="viewCleanedDataDisabled"
                  @click="viewCleanedFileData"
                >
                  <span class="ant-button-flex-content">
                    <span class="relative inline-block h-4 w-4" aria-hidden="true">
                      <SparklesIcon
                        class="absolute h-4 w-4"
                        stroke-width="1.5"
                        :class="{
                          color: !viewCleanedDataDisabled ? 'bg-[var(--background)]' : undefined
                        }"
                        style="left: -5px; top: -3px"
                      />
                      <Search
                        class="absolute h-3 w-3"
                        stroke-width="1.8"
                        :class="{
                          color: !viewCleanedDataDisabled ? 'bg-[var(--background)]' : undefined
                        }"
                        style="bottom: -3px; right: -2px"
                      />
                    </span>
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="viewCleanedDataDisabled"
                  @click="viewCleanedFileData"
                >
                  <span class="ant-button-flex-content">
                    <span class="relative inline-block h-4 w-4" aria-hidden="true">
                      <SparklesIcon
                        class="absolute h-4 w-4"
                        stroke-width="1.5"
                        :class="{
                          color: !viewCleanedDataDisabled ? 'bg-[var(--background)]' : undefined
                        }"
                        style="left: -5px; top: -3px"
                      />
                      <Search
                        class="absolute h-3 w-3"
                        stroke-width="1.8"
                        :class="{
                          color: !viewCleanedDataDisabled ? 'bg-[var(--background)]' : undefined
                        }"
                        style="bottom: -3px; right: -2px"
                      />
                    </span>

                    {{ 'View Cleaned Data' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- Submit Data for Cleaning  -->
              <a-tooltip :open="(selectedFile == null || !activeRules?.length || isProcessingRules || outputFileNameError?.length ||
                    rulesHaveValidationErrors) ? false : undefined">
                <template #title>
                  {{
                    selectedFile == null || !activeRules?.length
                      ? 'Select a file and add rules to process data cleaning rules.'
                      : !isProcessingRules
                        ? 'Process data cleaning rules'
                        : 'Processing data cleaning rules'
                  }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="
                    selectedFile == null ||
                    !activeRules?.length ||
                    isProcessingRules ||
                    outputFileNameError?.length ||
                    rulesHaveValidationErrors
                  "
                  @click="onSubmitDataForCleaningClicked"
                >
                  <Loader2 v-if="isProcessingRules" class="size-4 animate-spin" />
                  <SparklesIcon v-else class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="
                    selectedFile == null ||
                    !activeRules?.length ||
                    isProcessingRules ||
                    outputFileNameError?.length ||
                    rulesHaveValidationErrors
                  "
                  @click="onSubmitDataForCleaningClicked"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingRules" class="size-4 animate-spin" />
                    <SparklesIcon v-else class="h-4 w-4" />
                    {{ 'Process Rules' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View/Export JSON-->
              <a-tooltip placement="left">
                <template #title>
                  {{ 'View/Export JSON' }}
                </template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :class="{
                    'small-button':
                      selectedFile != null && activeRules?.length && !isProcessingRules
                  }"
                  :disabled="selectedFile == null || isProcessingRules"
                  @click="showJsonManagementDrawer"
                >
                  <CodeOutlined class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  :type="
                    selectedFile != null && activeRules?.length && !isProcessingRules
                      ? 'button'
                      : 'default'
                  "
                  :class="{
                    'small-button':
                      selectedFile != null && activeRules?.length && !isProcessingRules
                  }"
                  :disabled="selectedFile == null || isProcessingRules"
                  @click="showJsonManagementDrawer"
                >
                  <span class="ant-button-flex-content">
                    <CodeOutlined class="h-4 w-4" />
                    {{ 'View/Export JSON' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- Go to Analysis Config -->
              <a-tooltip placement="left" v-if="props.wizardModel != null">
                <template #title>Go to PK Analysis</template>
                <Button
                  v-if="is2XLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="goToAnalysisConfigDisabled"
                  @click="
                    () => {
                      goToWizardStepByType(AnalysisWizardStepType.AnalysisConfiguration)
                    }
                  "
                >
                  <Cog class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="goToAnalysisConfigDisabled"
                  @click="
                    () => {
                      goToWizardStepByType(AnalysisWizardStepType.AnalysisConfiguration)
                    }
                  "
                >
                  <span class="ant-button-flex-content">
                    {{ 'PK Analysis' }}
                    <ChevronRight class="h-4 w-4" />
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>
          <div
            v-if="(isLoadingAvailableRules || showLoading) && !wizardModel"
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading...</p>
            </div>
          </div>
          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="selectedFile == null && !hasErrorLoadingData"
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select a file to begin configuring rules
            </p>
            <div v-else-if="hasErrorLoadingData" class="mt-8 flex justify-center text-red-400">
              <WarningFilled class="mr-2 text-2xl" />
              An error was encountered loading rules or your analysis file.
            </div>

            <!-- Dynamic UI Field Groups Tabs (like Tables.vue) -->
            <div
              v-else-if="selectedRule != null && uiFieldGroups != null && uiFieldGroups?.length"
              class="min-h-0 flex-1"
            >
              <a-tabs
                :key="selectedRule?.id"
                v-model:activeKey="activeRuleTabKey"
                @change="onRuleViewModeTabChange"
                :destroyInactiveTabPane="true"
              >
                <a-tab-pane
                  v-for="(fieldGroup, index) in uiFieldGroups"
                  :key="fieldGroup.name"
                  :tab="fieldGroup.name"
                >
                  <RuleConfiguration
                    :configuration-level="1"
                    :rule="selectedRule"
                    :ui-field-group="fieldGroup"
                    :available-columns="availableColumns"
                    @configuration-updated="handleConfigurationUpdate"
                    :rules-schemas="availableRuleSchemas"
                    :show-description="index === 0"
                    :is-condensed-layout="true"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    DataCleaningTypeGuardService.isPayloadTemplateRuleSchema(selectedRule.schema)
                  "
                >
                  <div class="mt-[20px]">
                    <JsonRuleConfiguration
                      v-if="activeRuleEditConfiguration != null"
                      :key="selectedRule?.id"
                      :rule-schemas="availableRuleSchemas"
                      :available-columns="availableColumns"
                      v-model="activeRuleEditConfiguration"
                      :editor-schema="
                        DataCleaningJsonRuleConfigurationService.getJsonEditorSchemaForRule()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>

            <!-- Fallback: Old two-tab mode if no uiFieldGroups -->
            <a-tabs
              v-else-if="selectedRule != null"
              @change="onRuleViewModeTabChange"
              :key="selectedRule?.id"
              v-model:activeKey="activeRuleViewMode.mode"
              :destroyInactiveTabPane="true"
            >
              <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                <RuleConfiguration
                  :configuration-level="1"
                  :rule="selectedRule"
                  :available-columns="availableColumns"
                  @configuration-updated="handleConfigurationUpdate"
                  :rules-schemas="availableRuleSchemas"
                  :show-description="true"
                  :is-condensed-layout="true"
                />
              </a-tab-pane>
              <a-tab-pane
                key="edit"
                tab="JSON"
                :disabled="
                  DataCleaningTypeGuardService.isPayloadTemplateRuleSchema(selectedRule.schema)
                "
              >
                <div class="mt-[20px]">
                  <JsonRuleConfiguration
                    v-if="activeRuleEditConfiguration != null"
                    :key="selectedRule?.id"
                    :rule-schemas="availableRuleSchemas"
                    :available-columns="availableColumns"
                    v-model="activeRuleEditConfiguration"
                    :editor-schema="
                      DataCleaningJsonRuleConfigurationService.getJsonEditorSchemaForRule()
                    "
                    :place-holder="'Configure JSON'"
                    :configuration-level="1"
                    @update:modelValue="handleJsonEditorConfigurationChange"
                    :allow-type-change="false"
                  />
                </div>
              </a-tab-pane>
            </a-tabs>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <!-- Rule Selection Modal -->
    <RuleSelectionModal
      v-bind:configuration-level="1"
      v-model:visible="showRuleSelection"
      :rules-schemas="availableRuleSchemas"
      @rule-selected="addRule"
    />

    <!-- File Manager Modal -->
    <Modal
      v-model:open="showFileManager"
      title="Select a File"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <FileManager
          @file-selected="handleFileSelected"
          :selectable="true"
          title="Select a file for data cleaning"
          description="Click on a file to select it"
        />
      </div>
    </Modal>

    <!-- JSON Export Drawer -->
    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Data Cleaning JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <RuleJsonManagement
        v-if="selectedFile != null && availableRuleSchemas != null"
        :key="selectedRule?.id"
        :rule-schemas="availableRuleSchemas"
        :file-id="selectedFile.id!"
        :rules="activeRules"
        :output-file-name="outputFileName"
        :visibility-changed="showJsonManagement"
        :data-cleaning-json-parsing-service="dataCleaningJsonParsingService"
        @rules-applied="onJsonManagementRulesApplied"
      />
    </Drawer>

    <!-- Data Viewer Modal -->
    <FullScreenModal
      :open="dataViewerModalOpen"
      title="Data Viewer"
      :description="selectedFile?.name"
      @close="closeDataViewerModal"
    >
      <DataViewer :fileId="selectedFile?.id || ''" />
    </FullScreenModal>

    <!-- Cleaned Data Preview Modal -->
    <FullScreenModal
      :open="cleanedDataViewerModalOpen"
      title="Cleaned Data Preview"
      :description="cleanedFile?.name"
      @close="closeCleanedDataViewerModal"
    >
      <DataViewer :fileId="cleanedFile?.id || ''" />
    </FullScreenModal>
  </div>
</template>

<style scoped>
  /* make the button a flexible item and allow it to shrink in a flex row */
  .file-select-button {
    text-align: center;
    flex: 1 1 0; /* take remaining space but allow shrinking */
    min-width: 0; /* CRITICAL: allow shrinking so ellipsis can take effect */
  }

  /* Ensure the Ant button inner wrapper behaves as a constrained block */
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block; /* give the element a width */
    width: 100%; /* constrain to button width */
    overflow: hidden; /* required for ellipsis */
    white-space: nowrap; /* required for ellipsis */
    text-overflow: ellipsis; /* show "..." when truncated */
  }

  /* Rule moved highlight animation */
  .rule-moved-highlight {
    animation: ruleMovedPulse 1.5s ease-in-out;
    border: 2px solid var(--primary);
    background-color: var(--card-lighter) !important;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1) !important;
  }

  @keyframes ruleMovedPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
  }

  /* Rule deletion animation */
  .rule-deleting {
    animation: ruleDeletePulse 0.6s ease-in-out;
    border: 2px solid #ef4444 !important;
    background-color: var(--card) !important;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }

  @keyframes ruleDeletePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    25% {
      transform: scale(1.02);
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.3);
    }
    75% {
      transform: scale(0.98);
      box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
      opacity: 0;
    }
  }

  .data-cleaning-container {
    display: flex;
    flex-direction: column;
    min-height: 0; /* allow children to use min-height:0 so overflow works correctly */
  }

  /* apply flex fill only when layout mode requires filling the browser viewport */
  .data-cleaning-fill {
    flex: 1 1 0;
  }

  /* Ensure ant-tabs itself is a column flex container so content can grow/shrink */
  :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  /* Keep tab nav at natural height */
  :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }

  /* Make the content holder a flex child that can scroll when taller than available space.
     Use !important to override any upstream overflow rules from layout or global styles. */
  :deep(.ant-tabs .ant-tabs-content-holder),
  :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }

  /* Each tab pane should also allow internal scrolling */
  :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }

  /* left rules container becomes a flex column; inner .rules-scroll-area used for padding/content
     but the container itself will handle scrolling so it never grows beyond visible available height */
  .rules-list-container {
    display: flex;
    flex-direction: column;
    /* allow the container to take available space but not grow beyond it */
    flex: 1 1 auto;
    min-height: 0;
    /* ensure the container fills the available height and clamps to it */
    height: 100%;
    max-height: 100%;
    /* show vertical scrollbar when content overflows */
    overflow-y: auto;
  }

  /* Keep inner scroll area from creating nested scrollbars; it will flow inside the
     rules-list-container which now handles scrolling. */
  .rules-scroll-area {
    min-height: 0;
    overflow: visible;
  }

  /* Custom styling for PrimeVue Splitter */
  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }
  :deep(.p-splitter-panel) {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  :deep(.p-splitter-gutter) {
    background: #f0f0f0;
    border-left: 1px solid #d9d9d9;
    border-right: 1px solid #d9d9d9;
  }

  :deep(.p-splitter-gutter-handle) {
    background: #1890ff;
  }
</style>
